import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";

/**
 * Featured videos: metadata in `featured_videos` table, files in Storage (`featured-videos/...`).
 * Storage policies are unchanged — relies on your existing bucket rules.
 */

export type FeaturedVideo = {
  id: string;
  title: string;
  storageObjectName: string;
  uploadedByUserId: string;
  createdAt: string;
  updatedAt: string;
  url: string;
};

function getBucketName(): string {
  return import.meta.env.VITE_SUPABASE_STORAGE_BUCKET || "files";
}

function generateSimpleUniqueId(): string {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 9);
  return timestamp + randomPart;
}

async function getCurrentUserId(): Promise<string> {
  if (!isSupabaseConfigured) throw new Error("Supabase not configured.");
  const session = await getCurrentSession();
  if (!session?.user) throw new Error("User not authenticated.");
  return session.user.id;
}

function getVideoExtension(file: File): string {
  const byName = file.name?.includes(".") ? file.name.split(".").pop() : null;
  if (byName && byName.length <= 6) return byName.toLowerCase();

  const mime = file.type || "";
  if (mime === "video/mp4") return "mp4";
  if (mime === "video/webm") return "webm";
  if (mime === "video/quicktime") return "mov";
  if (mime === "video/x-matroska") return "webm";
  return "mp4";
}

function toFeaturedStorageObjectName(videoId: string, file: File): string {
  const ext = getVideoExtension(file);
  return `featured-videos/${videoId}.${ext}`;
}

function normalizeRow(row: Record<string, unknown>): Omit<FeaturedVideo, "url"> {
  return {
    id: String(row.id),
    title: String(row.title),
    storageObjectName: String(row.storage_object_name ?? row.storageObjectName),
    uploadedByUserId: String(row.uploaded_by_user_id ?? row.uploadedByUserId),
    createdAt: String(row.created_at ?? row.createdAt ?? ""),
    updatedAt: String(row.updated_at ?? row.updatedAt ?? ""),
  };
}

async function getSignedUrlForStorageObject(objectName: string): Promise<string> {
  const bucketName = getBucketName();
  const { data, error } = await supabase.storage
    .from(bucketName)
    .createSignedUrl(objectName, 60 * 60 * 24);

  if (error) {
    throw new Error(`Failed to generate video URL: ${error.message}`);
  }

  if (!data?.signedUrl) {
    throw new Error("Failed to generate video URL: no signedUrl returned");
  }

  return data.signedUrl;
}

class FeaturedVideosService {
  async getFeaturedVideos(): Promise<FeaturedVideo[]> {
    await getCurrentUserId();

    const { data, error } = await supabase
      .from("featured_videos")
      .select("id,title,storage_object_name,uploaded_by_user_id,created_at,updated_at")
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch featured videos: ${error.message}`);
    }

    const videos: FeaturedVideo[] = [];
    for (const row of data || []) {
      const base = normalizeRow(row as Record<string, unknown>);
      const url = await getSignedUrlForStorageObject(base.storageObjectName);
      videos.push({ ...base, url });
    }
    return videos;
  }

  async uploadFeaturedVideo(params: { file: File; title: string }): Promise<FeaturedVideo> {
    const userId = await getCurrentUserId();
    const { file, title } = params;
    if (!file) throw new Error("Missing file.");
    if (!title.trim()) throw new Error("Title is required.");

    const bucketName = getBucketName();
    const videoId =
      typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : generateSimpleUniqueId();
    const objectName = toFeaturedStorageObjectName(videoId, file);

    const arrayBuffer = await file.arrayBuffer();
    const fileBuffer = new Uint8Array(arrayBuffer);

    const { error: uploadError } = await supabase.storage
      .from(bucketName)
      .upload(objectName, fileBuffer, {
        contentType: file.type || "application/octet-stream",
        cacheControl: "3600",
        upsert: false,
      });

    if (uploadError) {
      throw new Error(`Failed to upload video: ${uploadError.message}`);
    }

    const { data: inserted, error: insertError } = await supabase
      .from("featured_videos")
      .insert({
        id: videoId,
        title: title.trim(),
        storage_object_name: objectName,
        uploaded_by_user_id: userId,
      })
      .select("id,title,storage_object_name,uploaded_by_user_id,created_at,updated_at")
      .single();

    if (insertError) {
      await supabase.storage.from(bucketName).remove([objectName]).catch(() => {});
      throw new Error(`Failed to save video metadata: ${insertError.message}`);
    }

    const base = normalizeRow(inserted as Record<string, unknown>);
    const url = await getSignedUrlForStorageObject(base.storageObjectName);
    return { ...base, url };
  }

  async renameFeaturedVideo(params: { id: string; title: string }): Promise<void> {
    await getCurrentUserId();
    const t = params.title.trim();
    if (!t) throw new Error("Title is required.");

    const { error } = await supabase
      .from("featured_videos")
      .update({ title: t })
      .eq("id", params.id);

    if (error) {
      throw new Error(`Failed to rename video: ${error.message}`);
    }
  }

  async deleteFeaturedVideo(id: string): Promise<boolean> {
    const bucketName = getBucketName();
    await getCurrentUserId();

    const { data: existing, error: fetchError } = await supabase
      .from("featured_videos")
      .select("id,storage_object_name")
      .eq("id", id)
      .single();

    if (fetchError) {
      throw new Error(`Failed to fetch video: ${fetchError.message}`);
    }
    if (!existing) return false;

    const objectName = String(
      (existing as { storage_object_name?: string }).storage_object_name ?? ""
    );

    if (objectName) {
      await supabase.storage.from(bucketName).remove([objectName]).catch(() => {});
    }

    const { error: deleteError } = await supabase.from("featured_videos").delete().eq("id", id);

    if (deleteError) {
      throw new Error(`Failed to delete video: ${deleteError.message}`);
    }

    return true;
  }
}

export const featuredVideosService = new FeaturedVideosService();
